import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    CalendarBlank,
    Chat,
    CheckSquare,
    FileText,
    Gavel,
    GitBranch,
    Pencil,
    Plus,
    Scales as ScalesSimple,
    Scroll,
    Sparkle,
    Upload,
    TrashSimple,
    WarningOctagon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { docsApi, draftsApi, mattersApi, researchApi, tasksApi, utilitiesApi, workflowApi } from "@/lib/api";
import { HearingForm } from "./Calendar";
import StrategyPanel from "@/components/StrategyPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MatterForm } from "./Matters";
import VoiceButton from "@/components/VoiceButton";
import { SourceChip, AIAssistBanner } from "@/components/SourceChip";

export default function MatterDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [query, setQuery] = useState("");
    const [qaLoading, setQaLoading] = useState(false);
    const [qa, setQa] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await mattersApi.get(id);
            setData(d);
        } catch {
            toast.error("Failed to load matter");
        }
        setLoading(false);
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const handleUpload = async (file) => {
        setUploading(true);
        try {
            await docsApi.upload(file, id);
            toast.success("Document uploaded and processed");
            await load();
        } catch (e) {
            console.error(e);
            toast.error("Upload failed");
        }
        setUploading(false);
    };

    const runCrossQuery = async () => {
        if (!query.trim()) return;
        setQaLoading(true);
        try {
            const res = await docsApi.crossQuery({ matter_id: id, query });
            setQa(res);
        } catch (e) {
            console.error(e);
            toast.error("Query failed");
        }
        setQaLoading(false);
    };

    const handleRemoveDoc = async (docId) => {
        if (!window.confirm("Remove this document?")) return;
        try {
            await docsApi.remove(docId);
            await load();
        } catch {
            toast.error("Failed to remove");
        }
    };

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading matter…</div>;
    if (!data) return <div className="p-8 text-sm">Not found.</div>;

    const m = data.matter;

    return (
        <div className="h-[calc(100vh-57px)] flex flex-col" data-testid="matter-detail">
            {/* header */}
            <div className="px-4 md:px-8 py-4 border-b border-border bg-white/70">
                <div className="max-w-[1600px] mx-auto">
                    <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2 -ml-2">
                        <ArrowLeft size={14} /> Back
                    </Button>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground tracking-wider uppercase mb-1">
                                <Gavel size={12} weight="duotone" />
                                {m.court || "Court TBD"} · {m.case_number || "No case number"}
                            </div>
                            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">{m.title}</h1>
                            <div className="text-sm text-foreground/80 mt-1">
                                {m.client_name || "—"}{" "}
                                <span className="text-muted-foreground">v.</span> {m.opposing_party || "—"}
                            </div>
                            <div className="flex items-center gap-2 mt-3 flex-wrap">
                                {m.practice_area && <span className="chip chip-doc">{m.practice_area}</span>}
                                {m.stage && <span className="chip chip-external">{m.stage}</span>}
                                {m.hearing_date && (
                                    <span className="chip chip-ai inline-flex items-center gap-1">
                                        <CalendarBlank size={11} /> Next: {m.hearing_date}
                                    </span>
                                )}
                                {(m.tags || []).map((t) => (
                                    <span key={t} className="chip chip-doc">{t}</span>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" size="sm" data-testid="edit-matter-btn">
                                        <Pencil size={14} /> Edit
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                        <DialogTitle className="font-serif">Edit matter</DialogTitle>
                                    </DialogHeader>
                                    <MatterForm
                                        initial={m}
                                        onDone={() => {
                                            setEditOpen(false);
                                            load();
                                        }}
                                    />
                                </DialogContent>
                            </Dialog>
                            <Button size="sm" onClick={() => navigate(`/drafting?new=1&matter=${m.id}`)} data-testid="new-draft-from-matter">
                                <Plus size={14} /> Draft
                            </Button>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => navigate(`/research?matter=${m.id}`)}
                                data-testid="research-from-matter"
                            >
                                <Sparkle size={14} /> Research
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* split pane */}
            <div className="flex-1 min-h-0 grid grid-cols-12 gap-0 max-w-[1600px] w-full mx-auto">
                {/* left: documents + matter tree */}
                <aside className="col-span-12 lg:col-span-3 border-r border-border bg-white/50 overflow-y-auto scrollbar-slim p-4" data-testid="matter-left-pane">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
                        Source Documents
                    </div>
                    <label className="block border border-dashed border-border rounded-sm p-4 text-center text-xs mb-4 cursor-pointer hover:bg-muted/50 transition-colors">
                        <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,.txt"
                            onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                            data-testid="matter-upload-input"
                        />
                        <Upload size={18} className="mx-auto text-muted-foreground mb-1" />
                        {uploading ? "Processing…" : "Click to upload PDF / DOCX / image"}
                    </label>

                    {!data.documents.length ? (
                        <div className="text-xs text-muted-foreground">No documents yet.</div>
                    ) : (
                        <div className="space-y-1">
                            {data.documents.map((d) => (
                                <div key={d.id} className="group flex items-start gap-2 p-2 rounded-sm hover:bg-muted/60">
                                    <Link to={`/documents/${d.id}`} className="flex items-start gap-2 flex-1 min-w-0">
                                        <FileText size={16} weight="duotone" className="mt-0.5 text-primary shrink-0" />
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{d.original_filename}</div>
                                            <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                                                <span className="chip chip-doc">{d.doc_type || "doc"}</span>
                                                <span>{d.language}</span>
                                                <span>· {d.num_pages}p</span>
                                                {d.used_ocr && <span className="chip chip-ai py-0">OCR</span>}
                                            </div>
                                        </div>
                                    </Link>
                                    <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" onClick={() => handleRemoveDoc(d.id)}>
                                        <TrashSimple size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </aside>

                {/* center: cross-matter chat / editor */}
                <section className="col-span-12 lg:col-span-6 flex flex-col overflow-hidden" data-testid="matter-center-pane">
                    <Tabs defaultValue="chat" className="flex-1 flex flex-col">
                        <TabsList className="mx-4 mt-3 self-start">
                            <TabsTrigger value="chat" data-testid="tab-chat"><Chat size={14} className="mr-1" /> Matter chat</TabsTrigger>
                            <TabsTrigger value="notes" data-testid="tab-notes"><Pencil size={14} className="mr-1" /> Notes</TabsTrigger>
                            <TabsTrigger value="workflow" data-testid="tab-workflow"><GitBranch size={14} className="mr-1" /> Workflow</TabsTrigger>
                            <TabsTrigger value="strategy" data-testid="tab-strategy"><Sparkle size={14} className="mr-1" /> Strategy</TabsTrigger>
                            <TabsTrigger value="activity" data-testid="tab-activity"><Scroll size={14} className="mr-1" /> Activity</TabsTrigger>
                        </TabsList>

                        <TabsContent value="chat" className="flex-1 min-h-0 overflow-hidden flex flex-col p-4">
                            <div className="text-xs text-muted-foreground mb-3">
                                Ask questions across <b>all documents in this matter</b>. Answers cite document name + page.
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-slim border border-border rounded-sm bg-white/70 p-4" style={{ maxHeight: "calc(100vh - 360px)" }}>
                                {!qa ? (
                                    <div className="text-sm text-muted-foreground">
                                        Try: <i>"Find all mentions of limitation"</i>,{" "}
                                        <i>"Show contradictions between the plaint and written statement"</i>.
                                    </div>
                                ) : (
                                    <div className="fade-in space-y-4">
                                        <div className="flex items-center gap-2">
                                            <SourceChip type="internal">Internal document analysis</SourceChip>
                                            <span className="text-xs text-muted-foreground">Not independently verified legal research.</span>
                                        </div>
                                        <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{qa.answer}</div>
                                        {!!qa.citations?.length && (
                                            <div className="border-t border-border pt-3">
                                                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                                                    Sources
                                                </div>
                                                <div className="space-y-2">
                                                    {qa.citations.map((c, i) => (
                                                        <div key={i} className="text-sm border border-border rounded-sm p-2 bg-white">
                                                            <div className="font-medium">{c.document} · p{c.page}</div>
                                                            <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{c.snippet}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="pt-3 flex items-center gap-2 badge-safe-row badge-safe-bottom">
                                <Input
                                    data-testid="matter-chat-input"
                                    placeholder="Ask across this matter's documents…"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && runCrossQuery()}
                                />
                                <VoiceButton
                                    compact
                                    onTranscript={({ text }) => setQuery(text)}
                                    testid="matter-chat-mic"
                                />
                                <Button onClick={runCrossQuery} disabled={qaLoading} data-testid="matter-chat-send">
                                    {qaLoading ? "…" : "Ask"}
                                </Button>
                            </div>
                        </TabsContent>

                        <TabsContent value="notes" className="p-4 overflow-y-auto">
                            <div className="text-xs text-muted-foreground mb-2">Free-form notes for this matter</div>
                            <Textarea
                                value={m.notes || ""}
                                onChange={(e) => setData({ ...data, matter: { ...m, notes: e.target.value } })}
                                rows={14}
                                className="font-serif text-base"
                                data-testid="matter-notes-area"
                            />
                            <div className="mt-3">
                                <Button size="sm" onClick={async () => {
                                    await mattersApi.update(m.id, m);
                                    toast.success("Notes saved");
                                }} data-testid="matter-notes-save">Save notes</Button>
                            </div>
                        </TabsContent>

                        <TabsContent value="workflow" className="p-4 overflow-y-auto" data-testid="workflow-tab-content">
                            <WorkflowPanel matter={m} onMatterChanged={(updated) => setData({ ...data, matter: updated })} />
                        </TabsContent>

                        <TabsContent value="strategy" className="p-4 overflow-y-auto" data-testid="strategy-tab-content">
                            <StrategyPanel matter={m} />
                        </TabsContent>

                        <TabsContent value="activity" className="p-4 overflow-y-auto">
                            {!data.activity.length ? (
                                <div className="text-sm text-muted-foreground">No activity recorded yet.</div>
                            ) : (
                                <div className="space-y-1">
                                    {data.activity.map((a) => (
                                        <div key={a.id} className="text-sm flex items-center justify-between border-b border-border py-2">
                                            <div><span className="font-mono text-xs text-primary">{a.action}</span></div>
                                            <div className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </section>

                {/* right: citations / research / drafts linked to matter */}
                <aside className="col-span-12 lg:col-span-3 border-l border-border bg-white/50 overflow-y-auto scrollbar-slim p-4" data-testid="matter-right-pane">
                    <AIAssistBanner />
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-4 mb-2">
                        Research memos
                    </div>
                    {!data.research.length ? (
                        <div className="text-xs text-muted-foreground mb-4">No memos linked.</div>
                    ) : (
                        <div className="space-y-2 mb-5">
                            {data.research.map((r) => (
                                <Link key={r.id} to={`/research?memo=${r.id}`} className="block border border-border rounded-sm p-2 hover:bg-muted/60">
                                    <div className="text-xs line-clamp-2">{r.query}</div>
                                    <div className="text-[10px] text-muted-foreground mt-1">{r.citations?.length || 0} citations</div>
                                </Link>
                            ))}
                        </div>
                    )}

                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
                        Drafts
                    </div>
                    {!data.drafts.length ? (
                        <div className="text-xs text-muted-foreground">No drafts.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.drafts.map((d) => (
                                <Link key={d.id} to={`/drafting/${d.id}`} className="block border border-border rounded-sm p-2 hover:bg-muted/60">
                                    <div className="text-xs font-medium line-clamp-2">{d.title}</div>
                                    <div className="text-[10px] text-muted-foreground mt-1">v{d.current_version}</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}

// ====================== Workflow Panel ===========================
// Three sub-panels: Stages, Limitation, Filing checklist + per-matter tasks

function WorkflowPanel({ matter, onMatterChanged }) {
    return (
        <div className="space-y-6">
            <MatterHearings matter={matter} />
            <StageWorkflow matter={matter} onChanged={onMatterChanged} />
            <LimitationCalculator matter={matter} onChanged={onMatterChanged} />
            <FilingChecklist matter={matter} onChanged={onMatterChanged} />
            <CourtFeeWidget matter={matter} />
            <CauseTitleWidget matter={matter} />
            <MatterTasks matter={matter} />
        </div>
    );
}

function MatterHearings({ matter }) {
    const [hearings, setHearings] = useState([]);
    const [open, setOpen] = useState(false);
    const [matters, setMatters] = useState([]);
    const load = () => import("@/lib/api").then((m) => m.hearingsApi.list({ matter_id: matter.id })).then(setHearings).catch(() => {});
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [matter.id]);
    useEffect(() => { import("@/lib/api").then((m) => m.mattersApi.list()).then(setMatters).catch(() => {}); }, []);
    const upcoming = hearings.filter((h) => h.status !== "cancelled" && h.scheduled_at >= new Date().toISOString());
    return (
        <section className="border border-border bg-white rounded-md p-4" data-testid="matter-hearings">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Gavel size={14} className="text-muted-foreground" />
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Hearings for this matter</div>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="add-hearing-btn"><Plus size={12} /> Add hearing</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                        <DialogHeader><DialogTitle>New hearing for {matter.title}</DialogTitle></DialogHeader>
                        <HearingForm matters={matters.length ? matters : [matter]} defaultMatterId={matter.id} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!hearings.length ? (
                <div className="text-sm text-muted-foreground">No hearings scheduled.</div>
            ) : (
                <ul className="space-y-1 text-sm">
                    {hearings.slice(0, 8).map((h) => (
                        <li key={h.id} className="flex items-center gap-2 py-1 border-b border-border/40" data-testid={`hearing-${h.id}`}>
                            <span className={`chip ${h.status === "scheduled" ? "chip-ai" : h.status === "held" ? "chip-ai text-emerald-700" : "chip-doc"}`}>{h.status}</span>
                            <span>{new Date(h.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                            <span className="text-xs text-muted-foreground">{h.court}{h.courtroom ? ` · ${h.courtroom}` : ""}{h.item_number ? ` · Item ${h.item_number}` : ""}</span>
                            {h.purpose && <span className="ml-auto text-xs text-muted-foreground">{h.purpose}</span>}
                        </li>
                    ))}
                </ul>
            )}
            <Link to="/calendar" className="text-xs text-muted-foreground hover:underline mt-2 inline-block">View calendar →</Link>
        </section>
    );
}

function CourtFeeWidget({ matter }) {
    const [cats, setCats] = useState([]);
    const [v, setV] = useState({ category: "civil_suit_money", claim_amount: "" });
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { import("@/lib/api").then((m) => m.utilitiesApi.courtFeeCategories()).then(setCats).catch(() => {}); }, []);
    const compute = async () => {
        setBusy(true);
        try {
            const payload = { category: v.category };
            if (v.claim_amount) payload.claim_amount = parseFloat(v.claim_amount);
            const r = await (await import("@/lib/api")).utilitiesApi.computeCourtFee(payload);
            setResult(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };
    const cat = cats.find((c) => c.key === v.category);
    const needsAmount = cat && cat.compute !== "fixed";
    return (
        <section className="border border-border bg-white rounded-md p-4" data-testid="court-fee-widget">
            <div className="flex items-center gap-2 mb-3">
                <ScalesSimple size={14} className="text-muted-foreground" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Court fee calculator</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                <Select value={v.category} onValueChange={(c) => setV({ ...v, category: c })}>
                    <SelectTrigger data-testid="cf-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
                {needsAmount && (
                    <Input type="number" placeholder="Amount in Rs" value={v.claim_amount} onChange={(e) => setV({ ...v, claim_amount: e.target.value })} data-testid="cf-amount" />
                )}
            </div>
            <Button size="sm" onClick={compute} disabled={busy || (needsAmount && !v.claim_amount)} data-testid="cf-compute">Compute</Button>
            {result && (
                <div className="mt-3 border border-border rounded-sm bg-muted/30 p-3 text-sm" data-testid="cf-result">
                    <div className="font-medium">Total: ₹ {result.total_inr.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                    {result.breakdown?.length > 0 && (
                        <details className="mt-1 text-xs"><summary className="cursor-pointer text-muted-foreground">Breakdown ({result.breakdown.length})</summary>
                            <ul className="mt-1 space-y-0.5 text-xs">
                                {result.breakdown.map((b, i) => <li key={i}>{b.label}: ₹{b.amount.toLocaleString("en-IN")}</li>)}
                            </ul>
                        </details>
                    )}
                    <div className="text-xs text-muted-foreground mt-2 italic">{result.notes}</div>
                </div>
            )}
        </section>
    );
}

function CauseTitleWidget({ matter }) {
    const [role, setRole] = useState("petitioner");
    const [md, setMd] = useState("");
    const [busy, setBusy] = useState(false);
    const gen = async () => {
        setBusy(true);
        try {
            const r = await (await import("@/lib/api")).utilitiesApi.causeTitle({ matter_id: matter.id, role });
            setMd(r.markdown);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };
    return (
        <section className="border border-border bg-white rounded-md p-4" data-testid="cause-title-widget">
            <div className="flex items-center gap-2 mb-3">
                <Scroll size={14} className="text-muted-foreground" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Cause-title generator</div>
            </div>
            <div className="flex items-end gap-2 mb-2">
                <div className="flex-1">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Our role</div>
                    <Select value={role} onValueChange={setRole}>
                        <SelectTrigger data-testid="ct-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="petitioner">Petitioner</SelectItem>
                            <SelectItem value="appellant">Appellant</SelectItem>
                            <SelectItem value="plaintiff">Plaintiff</SelectItem>
                            <SelectItem value="complainant">Complainant</SelectItem>
                            <SelectItem value="applicant">Applicant</SelectItem>
                            <SelectItem value="defendant">Defendant (filing WS)</SelectItem>
                            <SelectItem value="respondent">Respondent (filing reply)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button size="sm" onClick={gen} disabled={busy} data-testid="ct-generate">Generate</Button>
                {md && (
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(md); toast.success("Copied"); }}>Copy</Button>
                )}
            </div>
            {md && (
                <pre className="text-xs whitespace-pre-wrap font-serif border border-border rounded-sm p-3 bg-muted/30" data-testid="ct-output">{md}</pre>
            )}
        </section>
    );
}

function StageWorkflow({ matter, onChanged }) {
    const [templates, setTemplates] = useState([]);
    const [pickedKey, setPickedKey] = useState(matter.stage_template_key || "civil_suit");
    const [note, setNote] = useState("");
    const current = matter.status_stage || matter.stage || "—";
    useEffect(() => { workflowApi.stageTemplates().then(setTemplates).catch(() => {}); }, []);
    const tpl = templates.find((t) => t.key === pickedKey);
    const setStage = async (s) => {
        try {
            const updated = await workflowApi.setStage({ matter_id: matter.id, new_stage: s, note });
            onChanged?.(updated);
            setNote("");
            toast.success(`Stage → ${s}`);
        } catch {
            toast.error("Failed to set stage");
        }
    };
    return (
        <section className="border border-border bg-white rounded-md p-4" data-testid="stage-workflow">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Case stage</div>
                    <div className="font-serif text-xl">{current}</div>
                </div>
                <Select value={pickedKey} onValueChange={setPickedKey}>
                    <SelectTrigger className="w-[220px]" data-testid="stage-template-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            {tpl && (
                <div className="flex flex-wrap gap-1 mb-3" data-testid="stage-chips">
                    {tpl.stages.map((s) => (
                        <button
                            key={s}
                            onClick={() => setStage(s)}
                            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                                current === s
                                    ? "bg-amber-100 border-amber-400 text-amber-900 font-medium"
                                    : "bg-white border-border text-muted-foreground hover:border-foreground"
                            }`}
                            data-testid={`stage-${s.replace(/\s/g, "-").toLowerCase()}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}
            <Input
                placeholder="Optional note for this stage change"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="text-sm"
                data-testid="stage-note"
            />
            {!!matter.stage_history?.length && (
                <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Stage history ({matter.stage_history.length})</summary>
                    <ul className="mt-2 space-y-1 border-l-2 border-border pl-3">
                        {matter.stage_history.slice().reverse().map((h, i) => (
                            <li key={i} className="text-xs">
                                <span className="font-medium">{h.stage}</span> · {new Date(h.set_at).toLocaleString()}
                                {h.note && <span className="text-muted-foreground"> — {h.note}</span>}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </section>
    );
}

function LimitationCalculator({ matter, onChanged }) {
    const [causes, setCauses] = useState([]);
    const [key, setKey] = useState("ni_138_complaint");
    const [date, setDate] = useState("");
    const [desc, setDesc] = useState("");
    const [result, setResult] = useState(matter.limitation_facts || null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { workflowApi.limitationCauses().then(setCauses).catch(() => {}); }, []);

    const compute = async (persist = false) => {
        if (!date) { toast.error("Trigger date required"); return; }
        setBusy(true);
        try {
            const payload = key === "custom"
                ? { cause_of_action_key: null, trigger_date: date, description: desc }
                : { cause_of_action_key: key, trigger_date: date };
            const fn = persist ? workflowApi.saveLimitation : workflowApi.computeLimitation;
            const r = await fn(persist ? { matter_id: matter.id, ...payload } : payload);
            setResult(r);
            if (persist) {
                onChanged?.({ ...matter, limitation_facts: r });
                toast.success("Saved to matter");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to compute");
        }
        setBusy(false);
    };

    return (
        <section className="border border-border bg-white rounded-md p-4" data-testid="limitation-calculator">
            <div className="flex items-center gap-2 mb-3">
                <WarningOctagon size={16} weight="duotone" className="text-amber-700" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Limitation calculator</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                <Select value={key} onValueChange={setKey}>
                    <SelectTrigger data-testid="limitation-cause"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {causes.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                        <SelectItem value="custom">Custom (AI reasoning)</SelectItem>
                    </SelectContent>
                </Select>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="limitation-trigger-date" />
            </div>
            {key === "custom" && (
                <Textarea
                    rows={2}
                    placeholder="Describe the cause of action — the AI will reason about applicable Article of Limitation Act…"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    className="mb-2"
                    data-testid="limitation-custom-desc"
                />
            )}
            <div className="flex gap-2 mb-2">
                <Button size="sm" variant="outline" onClick={() => compute(false)} disabled={busy} data-testid="limitation-compute">Compute</Button>
                <Button size="sm" onClick={() => compute(true)} disabled={busy} data-testid="limitation-save">Save to matter</Button>
            </div>
            {result && (
                <div className={`border rounded-sm p-3 text-sm ${result.is_expired ? "border-destructive/40 bg-destructive/5" : (result.remaining_days != null && result.remaining_days <= 30 ? "border-amber-300 bg-amber-50/40" : "border-border bg-muted/30")}`} data-testid="limitation-result">
                    <div className="font-medium">{result.label}</div>
                    <div className="text-xs text-muted-foreground">{result.article} · {result.statute}</div>
                    {result.limitation_date && (
                        <div className="mt-1">
                            <span className="font-mono">{result.limitation_date}</span>
                            {result.remaining_days != null && (
                                <span className={`ml-2 chip ${result.is_expired ? "chip-doc text-destructive" : "chip-ai"}`}>
                                    {result.is_expired ? `Expired ${-result.remaining_days}d ago` : `${result.remaining_days}d remaining`}
                                </span>
                            )}
                        </div>
                    )}
                    {result.warning && <div className="mt-1 text-destructive text-xs">{result.warning}</div>}
                    {result.caveats && <div className="mt-1 text-xs text-muted-foreground">{result.caveats}</div>}
                </div>
            )}
        </section>
    );
}

function FilingChecklist({ matter, onChanged }) {
    const [templates, setTemplates] = useState([]);
    const [picked, setPicked] = useState(matter.filing_checklist_key || "");
    useEffect(() => { workflowApi.checklists().then(setTemplates).catch(() => {}); }, []);
    const tpl = templates.find((t) => t.key === (matter.filing_checklist_key || picked));
    const state = matter.filing_checklist_state || {};

    const assign = async () => {
        if (!picked) return;
        try {
            await workflowApi.assignChecklist({ matter_id: matter.id, checklist_key: picked });
            const updated = await mattersApi.get(matter.id);
            onChanged?.(updated.matter);
            toast.success("Checklist assigned");
        } catch {
            toast.error("Failed to assign");
        }
    };
    const toggle = async (key, checked) => {
        try {
            await workflowApi.toggleChecklistItem({ matter_id: matter.id, item_key: key, checked });
            const updated = await mattersApi.get(matter.id);
            onChanged?.(updated.matter);
        } catch {
            toast.error("Failed to update");
        }
    };

    const completed = tpl && state ? Object.values(state).filter(Boolean).length : 0;
    const total = tpl ? tpl.items.length : 0;
    return (
        <section className="border border-border bg-white rounded-md p-4" data-testid="filing-checklist">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Filing checklist</div>
                    <div className="font-serif text-xl">{tpl?.label || "(none)"}</div>
                </div>
                {!matter.filing_checklist_key && (
                    <div className="flex gap-2">
                        <Select value={picked} onValueChange={setPicked}>
                            <SelectTrigger className="w-[260px]" data-testid="checklist-pick"><SelectValue placeholder="Pick a template…" /></SelectTrigger>
                            <SelectContent>{templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" onClick={assign} disabled={!picked} data-testid="checklist-assign">Assign</Button>
                    </div>
                )}
            </div>
            {tpl && matter.filing_checklist_key && (
                <>
                    <div className="text-xs text-muted-foreground mb-2">{completed} of {total} complete</div>
                    <div className="w-full h-1 bg-border rounded-full overflow-hidden mb-3">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${total ? (completed / total) * 100 : 0}%` }} />
                    </div>
                    <ul className="space-y-1">
                        {tpl.items.map((it, idx) => {
                            const k = `item_${idx}`;
                            const checked = !!state[k];
                            return (
                                <li key={k} className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => toggle(k, e.target.checked)}
                                        className="accent-emerald-600 h-4 w-4"
                                        data-testid={`checklist-item-${idx}`}
                                    />
                                    <span className={checked ? "line-through text-muted-foreground" : ""}>{it}</span>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </section>
    );
}

function MatterTasks({ matter }) {
    const [tasks, setTasks] = useState([]);
    const [adding, setAdding] = useState(false);
    const [v, setV] = useState({ title: "", due_date: "", priority: "medium" });
    const load = () => tasksApi.list({ matter_id: matter.id }).then(setTasks).catch(() => {});
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [matter.id]);
    const create = async () => {
        if (!v.title.trim()) return;
        try {
            await tasksApi.create({ ...v, due_date: v.due_date || null, matter_id: matter.id });
            setV({ title: "", due_date: "", priority: "medium" });
            setAdding(false);
            load();
        } catch {
            toast.error("Failed");
        }
    };
    const toggle = async (t) => {
        try {
            await tasksApi.update(t.id, { status: t.status === "done" ? "open" : "done" });
            load();
        } catch { toast.error("Failed"); }
    };
    return (
        <section className="border border-border bg-white rounded-md p-4" data-testid="matter-tasks">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <CheckSquare size={14} className="text-muted-foreground" />
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Tasks for this matter</div>
                </div>
                {!adding && (
                    <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="matter-add-task"><Plus size={12} /> Add</Button>
                )}
            </div>
            {adding && (
                <div className="border border-border rounded-sm p-2 mb-3 space-y-2 bg-muted/30">
                    <Input placeholder="Task title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} className="text-sm" data-testid="matter-task-title" />
                    <div className="grid grid-cols-2 gap-2">
                        <Input type="date" value={v.due_date} onChange={(e) => setV({ ...v, due_date: e.target.value })} className="text-sm" data-testid="matter-task-due" />
                        <Select value={v.priority} onValueChange={(p) => setV({ ...v, priority: p })}>
                            <SelectTrigger data-testid="matter-task-priority"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="urgent">Urgent</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
                        <Button size="sm" onClick={create} data-testid="matter-task-submit">Add task</Button>
                    </div>
                </div>
            )}
            {!tasks.length ? (
                <div className="text-sm text-muted-foreground">No tasks yet.</div>
            ) : (
                <ul className="space-y-1">
                    {tasks.map((t) => (
                        <li key={t.id} className="flex items-center gap-2 text-sm py-1 border-b border-border/40" data-testid={`matter-task-${t.id}`}>
                            <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} className="accent-emerald-600 h-4 w-4" />
                            <span className={t.status === "done" ? "line-through text-muted-foreground" : ""}>{t.title}</span>
                            {t.due_date && <span className="ml-auto text-xs text-muted-foreground">{t.due_date}</span>}
                            <span className={`chip ${t.priority === "urgent" || t.priority === "high" ? "chip-doc text-destructive" : "chip-ai"}`}>{t.priority}</span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

